import { ViewIds, type ArdoqId } from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  availableReferenceFieldOptions,
  FieldSource,
  getBubbleChartZoomControls,
  getBubbleChartViewContext,
  getComponentTypeSelectConfig,
  getSelectedQuadrantDropdown,
  isLabelFilterValid,
  toBubbleChartSettingsBounds,
  type BubbleChartCommands,
  type BubbleChartDataPointModel,
  type BubbleChartPopovers,
  type BubbleChartRenderingData,
  type BubbleChartSettingsBounds,
  type BubbleChartViewSettings,
  type ComponentTypeSelectLabels,
  type FieldData,
  type FieldInfoKey,
  type FieldNameAndLabel,
  type FieldPickerArgs,
  type LabelFilter,
  type QuadrantZoomState,
  type BubbleChartSources,
} from '@ardoq/bubble-chart';
import { IconName } from '@ardoq/icons';
import { ChartWindowState } from '@ardoq/graph';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import {
  DropdownConfig,
  SettingsConfig,
  SettingsType,
  ViewContext,
} from '@ardoq/view-settings';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { isTruthy } from '@ardoq/common-helpers';
import { isBoolean, isObject, pick } from 'lodash';
import { Locale } from '@ardoq/locale';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { viewLegendCommands } from '@ardoq/view-legend';
import {
  getBubbleSizeScaleSliderControl,
  getLabelFilterButton,
  getLayoutDropdownConfig,
  showBubblesWithZeroValueConfig,
  sortedFieldNameOptions,
} from '../viewSettings/viewSettingsUtils';
import {
  getViewpointModeReferenceFieldOptionsLabels,
  getBubbleChartZoomControlsLabels,
  getComponentTypeSelectLabels,
  getSelectedQuadrantDropdownLabels,
  getTimeLayoutLabels,
} from 'locales/getBubbleChartLabels';
import { toViewModifiersWithSources } from './viewModifiersWithSources';

export type ViewpointModeBubbleChartSettingsControlsDependencies =
  BubbleChartSettingsBounds & {
    viewSettings: BubbleChartViewSettings;
    // Carried through for the "add to dashboard" entry point (viewModifiers$), which needs the
    // rendered data points and resolved axis titles to build the widget.
    data: BubbleChartDataPointModel[];
    xAxisTitle: string;
    yAxisTitle: string;
    /**
     * The same settings in the form they would be written back in. A setting
     * that names something the current data no longer holds is inert but still
     * stored, so consumers that read settings in order to set them again must
     * see the value the view actually applies instead.
     */
    settableViewSettings: SettableViewSettings;
    viewInstanceId: string;
    numericFields: FieldData[];
    availableBubbleComponentFields: FieldNameAndLabel[];
    availableReferenceFields: FieldNameAndLabel[];
    availableReferencedComponentFields: FieldNameAndLabel[];
    hasReferenceBubbleChartFeature: boolean;
    sources: BubbleChartSources | null;
    quadrantZoomState: QuadrantZoomState;
    windowState: ChartWindowState;
    locale: Locale;
    handles: {
      bubbleChartCommands: BubbleChartCommands;
      popovers: BubbleChartPopovers;
      getBubbleChartContentContainer: () => HTMLDivElement | null;
    };
  };

type SettableViewSettings = Omit<
  BubbleChartViewSettings,
  'labelFilter' | FieldInfoKey
> &
  Record<FieldInfoKey, string | null> & {
    /**
     * `null` rather than absent: serialising the AI manifest drops undefined
     * keys, and a dropped key reads as a setting the view does not support.
     */
    labelFilter: LabelFilter | null;
  };

const toSettableAxisField = (
  field: BubbleChartViewSettings[FieldInfoKey],
  componentAxisFieldNames: string[]
): string | null =>
  typeof field === 'string' && componentAxisFieldNames.includes(field)
    ? field
    : null;

type ToSettableViewSettingsArgs = {
  viewSettings: BubbleChartViewSettings;
  selectedComponentTypes: ArdoqId[];
  componentAxisFieldNames: string[];
};

const toSettableViewSettings = ({
  viewSettings,
  selectedComponentTypes,
  componentAxisFieldNames,
}: ToSettableViewSettingsArgs): SettableViewSettings => ({
  ...viewSettings,
  selectedComponentTypes,
  selectedFieldNameX: toSettableAxisField(
    viewSettings.selectedFieldNameX,
    componentAxisFieldNames
  ),
  selectedFieldNameY: toSettableAxisField(
    viewSettings.selectedFieldNameY,
    componentAxisFieldNames
  ),
  selectedFieldNameRadius: toSettableAxisField(
    viewSettings.selectedFieldNameRadius,
    componentAxisFieldNames
  ),
  labelFilter:
    viewSettings.labelFilter &&
    isLabelFilterValid(viewSettings.labelFilter, viewSettings)
      ? viewSettings.labelFilter
      : null,
});

export const toViewpointModeSettingsControlsDependencies = (
  state: BubbleChartRenderingData
): ViewpointModeBubbleChartSettingsControlsDependencies => {
  const {
    viewSettings,
    data,
    xAxisTitle,
    yAxisTitle,
    numericFields,
    viewpointModeAttributes,
    availableBubbleComponentFields,
    availableReferenceFields,
    availableReferencedComponentFields,
    hasReferenceBubbleChartFeature,
    sources,
    quadrantZoomState,
    viewInstanceId,
    popovers,
    locale,
  } = state;

  const settingsBounds = toBubbleChartSettingsBounds({
    viewSettings,
    data,
    numericFields,
    availableBubbleComponentFields,
    availableReferenceFields,
    availableReferencedComponentFields,
    componentTypeRegistry:
      viewpointModeAttributes?.componentTypeRegistry ?? null,
    isReferenceMode:
      hasReferenceBubbleChartFeature &&
      viewSettings.fieldSource === FieldSource.BY_REFERENCE,
  });

  return {
    ...settingsBounds,
    viewSettings,
    settableViewSettings: toSettableViewSettings({
      viewSettings,
      selectedComponentTypes: settingsBounds.selectedComponentTypes,
      componentAxisFieldNames: settingsBounds.componentAxisFieldNames,
    }),
    data,
    xAxisTitle,
    yAxisTitle,
    viewInstanceId,
    numericFields,
    availableBubbleComponentFields,
    availableReferenceFields,
    availableReferencedComponentFields,
    hasReferenceBubbleChartFeature,
    sources,
    quadrantZoomState,
    windowState: pick(state, ['windowCenter', 'windowScale']),
    locale,
    handles: {
      bubbleChartCommands: state.bubbleChartCommands,
      popovers,
      getBubbleChartContentContainer: state.getBubbleChartContentContainer,
    },
  };
};

export const isViewpointModeBubbleChartSettingsControlsDependencies = (
  viewProperties: Record<string, unknown>
): viewProperties is ViewpointModeBubbleChartSettingsControlsDependencies =>
  isObject(viewProperties.viewSettings) &&
  isObject(viewProperties.settableViewSettings) &&
  isObject(viewProperties.dimensionRanges) &&
  isObject(viewProperties.handles) &&
  typeof viewProperties.viewInstanceId === 'string' &&
  Array.isArray(viewProperties.componentTypesById) &&
  Array.isArray(viewProperties.selectedComponentTypes) &&
  Array.isArray(viewProperties.componentAxisFieldNames) &&
  Array.isArray(viewProperties.labelFilterTargets) &&
  [
    viewProperties.canSetComponentTypes,
    viewProperties.canSetComponentAxisFields,
    viewProperties.canSetLabelFilter,
  ].every(isBoolean);

type ToSettingsControlsArgs = {
  dependencies: ViewpointModeBubbleChartSettingsControlsDependencies;
  i18nLocale: I18nLocale;
};

const getComponentTypeConfig = (
  {
    componentTypesById,
    selectedComponentTypes,
    viewSettings,
  }: ViewpointModeBubbleChartSettingsControlsDependencies,
  labels: ComponentTypeSelectLabels
): DropdownConfig =>
  getComponentTypeSelectConfig({
    componentTypesById,
    selectedComponentTypes,
    onViewSettingsUpdate,
    fieldSource: viewSettings.fieldSource,
    labels,
  });

const getFieldPicker = (
  {
    numericFields,
    viewSettings,
    availableBubbleComponentFields,
    availableReferenceFields,
    availableReferencedComponentFields,
    hasReferenceBubbleChartFeature,
    isReferenceMode,
    hasFieldsInCurrentMode,
    locale,
  }: ViewpointModeBubbleChartSettingsControlsDependencies,
  i18nLocale: I18nLocale
) => {
  const fieldPickersDisabled = !hasFieldsInCurrentMode;

  return ({
    fieldName,
    additionalOptions = [],
    ...rest
  }: FieldPickerArgs): DropdownConfig => {
    const componentFieldOptions = !isReferenceMode
      ? [
          hasReferenceBubbleChartFeature &&
            numericFields.length > 0 && {
              type: DropdownOptionType.HEADER,
              label: t(
                i18nLocale,
                'tabview:bubbleChart.componentFields',
                'Component fields'
              ),
            },
          ...sortedFieldNameOptions(locale, {
            viewId: ViewIds.BUBBLE,
            list: numericFields,
            settingsKey: fieldName,
            isActive: name => viewSettings[fieldName] === name,
            onViewSettingsUpdate: (viewId, settings, persistent) =>
              onViewSettingsUpdate(
                viewId,
                { ...settings, labelFilter: undefined },
                persistent
              ),
          }),
        ].filter(isTruthy)
      : [];

    const referenceFieldOptions = isReferenceMode
      ? availableReferenceFieldOptions({
          availableBubbleComponentFields,
          availableReferenceFields,
          availableReferencedComponentFields,
          currentSettings: viewSettings,
          key: fieldName,
          locale,
          shouldClearLabelFilter: hasReferenceBubbleChartFeature,
          labelsProp: getViewpointModeReferenceFieldOptionsLabels(i18nLocale),
          resetFieldOnDeselect: true,
        })
      : [];

    return {
      id: fieldName,
      options: [
        ...componentFieldOptions,
        ...referenceFieldOptions,
        ...additionalOptions,
      ].filter(isTruthy),
      type: SettingsType.DROPDOWN,
      isDisabled: fieldPickersDisabled,
      popoverText: fieldPickersDisabled
        ? t(
            i18nLocale,
            'tabview:bubbleChart.fieldPickersDisabled',
            'There is no field in the selected field source.'
          )
        : undefined,
      maxDropdownSize: DropdownSize.M,
      ...rest,
    };
  };
};

export const toViewpointModeViewModifiers = ({
  dependencies,
  i18nLocale,
}: ToSettingsControlsArgs): SettingsConfig[] => {
  if (!dependencies.canSetComponentTypes) {
    return [];
  }

  const fieldPicker = getFieldPicker(dependencies, i18nLocale);
  const {
    viewSettings,
    sources,
    selectedComponentTypes,
    hasReferenceBubbleChartFeature,
  } = dependencies;

  if (hasReferenceBubbleChartFeature && sources) {
    return toViewModifiersWithSources({
      sources,
      selectedComponentTypes,
      viewSettings,
      fieldPicker,
      i18nLocale,
    });
  }

  return [
    getComponentTypeConfig(
      dependencies,
      getComponentTypeSelectLabels(i18nLocale)
    ),
    fieldPicker({
      fieldName: 'selectedFieldNameX',
      label: t(i18nLocale, 'tabview:bubbleChart.xAxis', 'X-axis'),
      iconName: IconName.ARROW_RIGHT_ALT,
    }),
    fieldPicker({
      fieldName: 'selectedFieldNameY',
      label: t(i18nLocale, 'tabview:bubbleChart.yAxis', 'Y-axis'),
      iconName: IconName.ARROW_TOP_ALT,
    }),
    fieldPicker({
      fieldName: 'selectedFieldNameRadius',
      label: t(
        i18nLocale,
        'tabview:bubbleChart.bubbleSizeLabel',
        'Bubble size'
      ),
      iconName: IconName.BUBBLE_CHART,
    }),
    getLayoutDropdownConfig(viewSettings, i18nLocale),
    showBubblesWithZeroValueConfig(viewSettings, i18nLocale),
  ].filter(isTruthy);
};

export const toViewpointModeViewActions = ({
  dependencies,
  i18nLocale,
}: ToSettingsControlsArgs): SettingsConfig[] => {
  const {
    viewSettings,
    dimensionRanges,
    numericFields,
    availableBubbleComponentFields,
    availableReferenceFields,
    availableReferencedComponentFields,
    quadrantZoomState,
    windowState,
    viewInstanceId,
    hasFieldsInCurrentMode,
    handles: { bubbleChartCommands, popovers },
  } = dependencies;

  const hasSelectedFields =
    viewSettings.selectedFieldNameX ||
    viewSettings.selectedFieldNameY ||
    viewSettings.selectedFieldNameRadius;

  return [
    getBubbleSizeScaleSliderControl(
      viewSettings.bubbleAreaScaleFactor,
      i18nLocale
    ),
    getSelectedQuadrantDropdown(
      viewSettings,
      quadrantZoomState,
      viewInstanceId,
      windowState,
      popovers.disabledZoomToQuadrantPopover.popoverContent,
      getSelectedQuadrantDropdownLabels(i18nLocale),
      getTimeLayoutLabels(i18nLocale)
    ),
    getLabelFilterButton({
      dimensionRanges,
      viewSettings,
      availableBubbleComponentFields,
      availableReferenceFields,
      availableReferencedComponentFields,
      numericFields,
      isDisabled: !hasSelectedFields || !hasFieldsInCurrentMode,
      i18nLocale,
    }),
    ...getBubbleChartZoomControls({
      ...windowState,
      viewInstanceId,
      bubbleChartCommands,
      labels: getBubbleChartZoomControlsLabels(i18nLocale),
    }),
  ].filter(isTruthy);
};

export const toViewpointModeViewContext = ({
  viewSettings,
  viewInstanceId,
  handles: { getBubbleChartContentContainer },
}: ViewpointModeBubbleChartSettingsControlsDependencies): ViewContext =>
  getBubbleChartViewContext({
    viewLegendCommands,
    getBubbleChartContentContainer,
    isLegendActive: viewSettings.isLegendActive,
    zoomControls: null,
    viewInstanceId,
  });
